/**
 * Auxiliary work-list chrome, split out of work_filters.ts to keep that
 * file under the max-line-count structural rule (same reason work_state.ts
 * was extracted from work.ts).
 *
 * Holds: the People (assignee) filter group and its active chip, the
 * page-header subtitle, and the pagination controls. All are pure HTML
 * renderers wired through the `data-action` grammar like the rest of the
 * filter bar.
 */

import { esc } from '../../shared/rendering.js'
import { action } from '../components/action-delegation.js'
import type { WorkListFilters } from './work_filters.js'
import type { WorkStats, WorkListMeta } from '../../api/work.js'

/** One People-group button. `arg === null` clears; '@me' is the self sentinel. */
function personBtn(arg: string | null, label: string, active: boolean): string {
  const argStr = arg ?? 'null'
  return `<button class="wi-filter-btn" aria-pressed="${active}" ${action('setWorkAssignee', argStr)}>${esc(label)}</button>`
}

/**
 * The People (assignee) filter group: Anyone · Me · one button per distinct
 * assignee. "Me" sends the '@me' sentinel — the server resolves it to the
 * caller, so the SPA stays identity-blind and never sends a name.
 */
export function renderPeopleGroup(f: WorkListFilters, assignees: readonly string[]): string {
  const btns = [
    personBtn(null, 'Anyone', f.assignee === null),
    personBtn('@me', 'Me', f.assignee === '@me'),
    ...assignees.map((a) => personBtn(a, a, f.assignee === a)),
  ].join('')
  return `<div class="wi-filter-row">
    <div role="group" aria-label="Filter by assignee" class="wi-filter-group">${btns}</div>
  </div>`
}

/** Active-filter chip for the assignee filter. '@me' shows as "Me". */
export function renderPeopleChip(assignee: string): string {
  const label = assignee === '@me' ? 'Me' : assignee
  return `<span class="wi-chip" style="--wi-chip-bg:var(--cyn-d);--wi-chip-c:var(--cyn)"><span aria-hidden="true">👤</span> ${esc(label)}</span>`
}

/** Page-header subtitle metadata — responsive to active filters. */
export function renderWorkSubtitle(
  shown: number,
  total: number | undefined,
  stats: WorkStats | undefined,
  hasActiveFilter: boolean,
): string {
  const shownText = hasActiveFilter && total !== undefined ? `${shown} of ${total}` : `${shown}`
  const totalLabel =
    shownText + (shownText.endsWith('1') && !shownText.endsWith('11') ? ' item' : ' items')
  const meta = stats
    ? `<span class="sep">·</span>${stats.in_progress} in progress<span class="sep">·</span>${stats.open} open<span class="sep">·</span>${stats.done} done`
    : ''
  return `<span class="wi-subtitle-meta">${esc(totalLabel)}${meta}</span>`
}

/** Pagination controls. `page` is 1-based per server contract. */
export function renderWorkPagination(meta: WorkListMeta | undefined, fallbackPage: number): string {
  if (!meta) {
    return ''
  }
  const page = meta.page ?? fallbackPage
  const total = meta.total
  const pageSize = meta.page_size ?? 50
  const hasMore = meta.has_more ?? (total !== undefined && page * pageSize < total)
  if (page <= 1 && !hasMore) {
    return ''
  }
  const prevDisabled = page <= 1
  const nextDisabled = !hasMore
  const btn =
    'font-family:var(--mono);font-size:11px;padding:5px 14px;border-radius:var(--r2);background:var(--s2);border:1px solid var(--bd);color:var(--t2);cursor:pointer;transition:all var(--t-fast) var(--ease);'
  const disabled = 'opacity:0.4;cursor:not-allowed;'
  return `<nav aria-label="Work list pagination" style="display:flex;align-items:center;gap:12px;padding:16px;justify-content:center">
    <button ${prevDisabled ? 'disabled' : ''} style="${btn}${prevDisabled ? disabled : ''}" ${action('setWorkPage', String(page - 1))} aria-label="Previous page">← Prev</button>
    <span style="font-family:var(--mono);font-size:11px;color:var(--t3)" aria-live="polite">Page ${page}${total !== undefined ? ` · ${total} total` : ''}</span>
    <button ${nextDisabled ? 'disabled' : ''} style="${btn}${nextDisabled ? disabled : ''}" ${action('setWorkPage', String(page + 1))} aria-label="Next page">Next →</button>
  </nav>`
}
